import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../common/ui';
import { formatDateTime } from '../../utils/dateUtils';
import type { GkFinding, GkResolution } from './types';

/**
 * Accountant decisions for items that hold a GK export day
 * (GK_EXPORT_PLAN.md 2.9, milestone M10): the "Resolve" form of an exception
 * and the list of open decisions.
 */

const inputClass =
  'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelClass = 'mb-1 text-xs font-medium text-gray-500';

type Action = 'advance' | 'book_konto' | 'use_invoice' | 'refund_to_invoice';

const ACTION_LABEL: Record<Action, string> = {
  advance: 'Advance (receipt without invoice)',
  book_konto: 'Book on a konto',
  use_invoice: 'Book on an invoice',
  refund_to_invoice: 'Refund on an invoice (no credit note)',
};

const KONTO_LABEL: Record<string, string> = {
  '120000': '120000 receivable, domestic',
  '121000': '121000 receivable, foreign (currency)',
  '121100': '121100 receivable, EU / advances',
  '110900': '110900 own transfer / exchange',
  '285100': '285100 refund of a card purchase',
};

/** Which decisions fit an exception (by code and item). */
export function actionsFor(f: GkFinding): Action[] {
  if (!f.paymentId || !f.itemKey) return [];
  const refundItem = /^(rt|bt|pp):/.test(f.itemKey);
  switch (f.code) {
    case 'B1':
      return refundItem ? ['advance', 'book_konto'] : ['advance', 'book_konto', 'use_invoice'];
    case 'B6':
      return f.itemKey === 'payment' ? ['book_konto', 'advance', 'use_invoice'] : [];
    case 'B10':
    case 'B12':
    case 'B24':
      return f.itemKey.startsWith('order:') ? ['use_invoice'] : [];
    case 'B4':
      return refundItem ? ['refund_to_invoice'] : [];
    case 'B15':
      return f.itemKey === 'carryover' ? ['advance'] : [];
    default:
      return [];
  }
}

type Partner = { sifra: number; naziv1: string | null };
let partnersCache: Partner[] | null = null;
let kontosCache: Record<string, { partner: 'required' | 'forbidden' }> | null = null;

/** "4307/2026" -> [4307, 2026]; anything else -> null. */
function parseInvoice(text: string): [number, number] | null {
  const m = /^\s*(\d{1,7})\s*\/\s*(\d{4})\s*$/.exec(text);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

export const ResolveForm: React.FC<{ finding: GkFinding; onSaved: () => void; onClose: () => void }> = ({ finding, onSaved, onClose }) => {
  const actions = actionsFor(finding);
  const [action, setAction] = useState<Action>(actions[0]);
  const [konto, setKonto] = useState('');
  const [partner, setPartner] = useState('');
  const [invoice, setInvoice] = useState('');
  const [note, setNote] = useState('');
  const [partners, setPartners] = useState<Partner[]>(partnersCache || []);
  const [kontos, setKontos] = useState(kontosCache || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!partnersCache) {
      axios.get<{ partners: Partner[] }>('/api/accounting/gk-export/partners').then((res) => {
        partnersCache = res.data.partners;
        setPartners(res.data.partners);
      }).catch(() => {});
    }
    if (!kontosCache) {
      axios.get<{ allowedKontos: Record<string, { partner: 'required' | 'forbidden' }> }>('/api/accounting/gk-export/resolutions').then((res) => {
        kontosCache = res.data.allowedKontos;
        setKontos(res.data.allowedKontos);
      }).catch(() => {});
    }
  }, []);

  const needsPartner = action === 'advance' || (action === 'book_konto' && kontos[konto]?.partner === 'required');
  const invoiceAction = action === 'use_invoice' || action === 'refund_to_invoice';
  const parsed = parseInvoice(invoice);
  const invoiceInvalid = invoice.trim() !== '' && !parsed;
  const missing =
    !note.trim()
    || (action === 'book_konto' && !konto)
    || (needsPartner && !partner)
    || (action === 'use_invoice' && !parsed)
    || invoiceInvalid;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post('/api/accounting/gk-export/resolutions', {
        paymentId: finding.paymentId,
        itemKey: finding.itemKey,
        action,
        konto: action === 'book_konto' || invoiceAction ? konto || null : null,
        // Send the partner only while its field is shown; the server refuses a partner it does not expect.
        partnerSifra: needsPartner || invoiceAction ? partner || null : null,
        invoiceSeq: invoiceAction && parsed ? parsed[0] : null,
        invoiceYear: invoiceAction && parsed ? parsed[1] : null,
        note: note.trim(),
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the decision.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label htmlFor={`act-${finding.paymentId}-${finding.itemKey}`} className={labelClass}>Decision</label>
          <select id={`act-${finding.paymentId}-${finding.itemKey}`} value={action} onChange={(e) => setAction(e.target.value as Action)} className={inputClass}>
            {actions.map((a) => (
              <option key={a} value={a}>
                {finding.code === 'B15' && a === 'advance' ? 'Book on the customer (receivable konto)' : ACTION_LABEL[a]}
              </option>
            ))}
          </select>
        </div>
        {(action === 'book_konto' || invoiceAction) && (
          <div className="flex flex-col">
            <label htmlFor={`konto-${finding.paymentId}`} className={labelClass}>{invoiceAction ? 'Konto (only without GK posting)' : 'Konto'}</label>
            <select id={`konto-${finding.paymentId}`} value={konto} onChange={(e) => setKonto(e.target.value)} className={inputClass}>
              <option value="">–</option>
              {Object.keys(kontos)
                .filter((k) => !invoiceAction || ['120000', '121000', '121100'].includes(k))
                .map((k) => <option key={k} value={k}>{KONTO_LABEL[k] || k}</option>)}
            </select>
          </div>
        )}
        {invoiceAction && (
          <div className="flex flex-col">
            <label htmlFor={`inv-${finding.paymentId}`} className={labelClass}>
              {action === 'refund_to_invoice' ? 'Invoice (number/year; empty = the order\'s invoice)' : 'Invoice (number/year)'}
            </label>
            <input id={`inv-${finding.paymentId}`} placeholder="number/year" value={invoice} onChange={(e) => setInvoice(e.target.value)} className={`${inputClass} w-36`} />
          </div>
        )}
        {(needsPartner || invoiceAction) && (
          <div className="flex flex-col">
            <label htmlFor={`partner-${finding.paymentId}`} className={labelClass}>{needsPartner ? 'Partner (GK code)' : 'Partner (only without GK posting)'}</label>
            <input
              id={`partner-${finding.paymentId}`}
              list="gk-partner-list"
              value={partner}
              onChange={(e) => setPartner(e.target.value.replace(/[^\d]/g, ''))}
              className={`${inputClass} w-32`}
            />
            <datalist id="gk-partner-list">
              {partners.map((p) => <option key={p.sifra} value={p.sifra}>{p.naziv1 || ''}</option>)}
            </datalist>
          </div>
        )}
        <div className="flex min-w-[16rem] flex-1 flex-col">
          <label htmlFor={`note-${finding.paymentId}`} className={labelClass}>Note (why)</label>
          <input id={`note-${finding.paymentId}`} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
        </div>
        <Button onClick={submit} disabled={busy || missing}>{busy ? 'Saving…' : 'Save decision'}</Button>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
      </div>
      {invoiceInvalid && <p className="mt-1 text-red-700">Write the invoice as number/year, for example 5078/2026.</p>}
      {error && <p className="mt-1 text-red-700">{error}</p>}
    </div>
  );
};

const RECEIVABLE_KONTOS = ['120000', '121000', '121100'];

/**
 * The B2 form of the payment page: saves a manual row of the partner konto
 * map (the table "Receivable konto per partner" of the GK export page). It is
 * not a decision for this payment: the konto is used for every invoice of the
 * partner that has no GK posting. A partner without a stored GK partner block
 * gets B3 after the save; only an upload that contains the partner fixes that.
 */
export const PartnerKontoForm: React.FC<{ partnerSifra: number; onSaved: () => void; onClose: () => void }> = ({ partnerSifra, onSaved, onClose }) => {
  const [konto, setKonto] = useState('');
  const [rule, setRule] = useState<'none' | 'order_currency'>('none');
  // null while the check runs or when it failed: then no hint is shown.
  const [hasBlock, setHasBlock] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    axios.get(`/api/accounting/gk-export/partners/${partnerSifra}/block`)
      .then(() => { if (live) setHasBlock(true); })
      .catch((err) => { if (live && err?.response?.status === 404) setHasBlock(false); });
    return () => { live = false; };
  }, [partnerSifra]);

  const chooseKonto = (k: string) => {
    setKonto(k);
    // The rows of the map with 121000 use the order currency, the others none.
    setRule(k === '121000' ? 'order_currency' : 'none');
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.put(`/api/accounting/gk-export/partner-konto/${partnerSifra}`, { konto, sifraValuteRule: rule });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not save the konto.');
    } finally {
      setBusy(false);
    }
  };

  const name = (partnersCache || []).find((p) => p.sifra === partnerSifra)?.naziv1;
  return (
    <div className="mt-1 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="partner-konto-form">
      <p className="mb-2 text-gray-700">
        This sets the receivable konto for all invoices of partner {partnerSifra}{name ? ` (${name})` : ''} that have no GK posting.
        It is saved as a manual row of the konto map on the <Link to="/accounting/gk-export" className="text-blue-700 underline">GK export page</Link>.
      </p>
      {hasBlock === false && (
        <p className="mb-2 rounded-md bg-amber-50 px-3 py-2 text-amber-800" data-testid="partner-konto-no-block">
          Partner {partnerSifra} is not in the GK partner list. A konto alone does not release this payment: B3 comes next.
          Upload a GK export or FAW file that contains this partner.
        </p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label htmlFor={`pk-konto-${partnerSifra}`} className={labelClass}>Konto</label>
          <select id={`pk-konto-${partnerSifra}`} value={konto} onChange={(e) => chooseKonto(e.target.value)} className={inputClass}>
            <option value="">–</option>
            {RECEIVABLE_KONTOS.map((k) => <option key={k} value={k}>{KONTO_LABEL[k]}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label htmlFor={`pk-rule-${partnerSifra}`} className={labelClass}>Currency on the line</label>
          <select id={`pk-rule-${partnerSifra}`} value={rule} onChange={(e) => setRule(e.target.value as 'none' | 'order_currency')} className={inputClass}>
            <option value="none">none</option>
            <option value="order_currency">order currency (121000)</option>
          </select>
        </div>
        <Button onClick={submit} disabled={busy || !konto}>{busy ? 'Saving…' : 'Save konto'}</Button>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
      </div>
      {error && <p className="mt-1 text-red-700">{error}</p>}
    </div>
  );
};


/** "4.99 USD" from a transaction amount (Braintree stores 5 decimals). */
function money(amount: string | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
}

/** What the decision is about, in words: the refund, the transaction, the order's payment, the whole payment. */
export function decisionItemText(r: GkResolution): string {
  const item = r.item;
  if (!item) return r.item_key;
  if (item.kind === 'transaction') {
    const what = item.isRefund === false ? 'transaction' : 'refund';
    const amount = money(item.txnAmount, item.txnCurrency);
    return `${item.source} ${what} ${item.txnId}${amount ? `, ${amount}` : ''}`;
  }
  if (item.kind === 'order') return `Payment of order #${item.orderNumber}`;
  if (item.kind === 'payment') return 'The whole payment';
  if (item.kind === 'carryover') return 'Revolut carryover (balance kept between payouts)';
  return r.item_key;
}

/** The decision in words, with its konto, invoice and partner. */
export function decisionText(r: GkResolution): string {
  const invoice = r.invoice_seq ? `${r.invoice_seq}/${r.invoice_year}` : null;
  let text: string;
  switch (r.action) {
    case 'advance':
      text = 'Advance (receipt without invoice)';
      break;
    case 'book_konto':
      text = `Book on konto ${r.konto}`;
      break;
    case 'use_invoice':
      text = `Book on invoice ${invoice}`;
      break;
    case 'refund_to_invoice':
      text = invoice ? `Refund on invoice ${invoice}` : "Refund on the order's invoice";
      break;
    case 'accept_suggestion':
      text = 'Accept the suggestion';
      break;
    default:
      text = r.action;
  }
  if (r.partner_sifra !== null) text += `, partner ${r.partner_sifra}${r.partner_name ? ` ${r.partner_name}` : ''}`;
  return text;
}

/** The facts the decision belongs to: day, order, Vasco documents of the order. */
function headerParts(r: GkResolution): string[] {
  const item = r.item;
  const parts = [r.day];
  if (item?.orderNumber) parts.push(`Order #${item.orderNumber}`);
  const docs = item ? [...item.invoices, ...item.creditNotes.map((n) => `credit note ${n}`)] : [];
  if (docs.length > 0) parts.push(`Vasco ${docs.join(', ')}`);
  return parts;
}

/**
 * The open decisions, with Remove (not for exported payments). One block per
 * decision: the facts it belongs to on top, then what it is about, the
 * decision and the note. With `paymentId` only the decisions of that payment
 * (the payment page); without it each block also names the payment.
 */
export const GkDecisions: React.FC<{ refreshKey: number; enabled: boolean; onChanged?: () => void; paymentId?: number }> = ({ refreshKey, enabled, onChanged, paymentId }) => {
  const [rows, setRows] = useState<GkResolution[]>([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get<{ resolutions: GkResolution[] }>(
        '/api/accounting/gk-export/resolutions',
        paymentId ? { params: { paymentId } } : undefined
      );
      setRows(res.data.resolutions);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not load the decisions.');
    }
  }, [paymentId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const remove = async (id: number) => {
    setError('');
    try {
      await axios.delete(`/api/accounting/gk-export/resolutions/${id}`);
      await load();
      if (onChanged) onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not remove the decision.');
    }
  };

  if (rows.length === 0 && !error) {
    return (
      <p className="text-sm text-gray-500">
        {paymentId ? 'No decisions for this payment.' : 'No decisions yet. Use "Resolve" at an exception in the preview.'}
      </p>
    );
  }
  return (
    <div>
      {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
      <ul className="divide-y divide-gray-100">
        {rows.map((r) => (
          <li key={r.id} className="flex items-start justify-between gap-4 py-3 text-sm" data-testid="gk-decision">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-gray-900">
                {!paymentId && (
                  <>
                    <Link to={`/accounting/payments/${r.payment_id}`} className="text-blue-600 hover:underline">
                      Payment {r.payment_id}
                    </Link>
                    {` · ${r.counterparty || ''} · ${r.amount} ${r.currency} · `}
                  </>
                )}
                {headerParts(r).join(' · ')}
              </div>
              <dl className="mt-1 grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-0.5">
                <dt className="text-gray-500">Item</dt>
                <dd className="break-words text-gray-800">{decisionItemText(r)}</dd>
                <dt className="text-gray-500">Decision</dt>
                <dd className="text-gray-800">{decisionText(r)}</dd>
                <dt className="text-gray-500">Note</dt>
                <dd className="break-words text-gray-800">
                  {r.note}
                  <span className="text-gray-500">
                    {` · ${r.created_by_name || 'unknown'}, ${formatDateTime(r.created_at)}`}
                  </span>
                </dd>
              </dl>
            </div>
            <div className="flex-shrink-0">
              {r.exported ? (
                <span className="text-xs text-gray-500" title="The payment is exported to GK, so the decision cannot be removed.">Exported</span>
              ) : (
                <Button variant="secondary" icon={Trash2} onClick={() => remove(r.id)} disabled={!enabled}>Remove</Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
